/* eslint-disable max-len */
import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import useStripeValidations from '../../../common/hooks/useStripeValidations';
import { useFormControls } from '../../../common/hooks/useFormControls';
import PaymentInfo from '../../../common/components/PaymentInfo/PaymentInfo';
import BillingContactInfo from '../../../common/components/BillingContactInfo';
import SubSection from '../../../common/components/SubSection';
import MessageBanner from '../../../common/components/MessageBanner';
import LoadingScreen from '../../../common/LoadingScreen';
import Button from '../../../common/Button';

import PriceSummaryBox from '../../../common/components/PriceSummaryBox';
import ArticleLicensingService from '../services/ArticleLicensingService';
import { billingContactForm, updateBillingContactValidators } from '../forms/billingContactForm';
import styles from './PaymentScreen.module.scss';
import { licensingURL } from '../constants';
import ArticleLicensingTermsField from '../components/ArticleLicensingTermsField/ArticleLicensingTermsField';

const PaymentScreen = () => {
  const { formValues, newspost, pushEvent } = useOutletContext();
  const {
    articleLicensingContactEmail,
    articleLicensingPriceInDollars,
    site,
  } = window.articleLicensing;

  const policySiteParam = site?.name ? `?pub=${site.name.split(' ').join('')}.com` : '';
  const navigate = useNavigate();

  const {
    fields, setField, setValidators, validateField, validateForm, resetForm,
  } = useFormControls(billingContactForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showNonFieldError, setShowNonFieldError] = useState(false);

  // STRIPE PAYMENT HANDLING //
  const {
    cardFields,
    handleCardNumberChange,
    handleCardExpiryChange,
    handleCardCVCChange,
    validatePaymentInfo,
    confirmCardPayment,
  } = useStripeValidations();

  const [billingContactInfoNeeded, setBillingContactInfoNeeded] = useState(false);
  const setBillingContactInfoNeededOnChange = (e) => {
    const checkboxValue = e.target.value === 'true';
    setBillingContactInfoNeeded(checkboxValue);
    updateBillingContactValidators(checkboxValue, setValidators, resetForm);
  };

  const articleLicensingApi = new ArticleLicensingService();
  const handleSubmitClick = async (e) => {
    e.preventDefault();
    // Using bitwise AND in the line below to prevent short-circuiting
    // eslint-disable-next-line no-bitwise
    const isValid = validateForm() & validatePaymentInfo();
    if (isValid && !submitted) {
      setIsSubmitting(true);
      setShowNonFieldError(false);
      try {
        // submit the article license object to backend
        const postResponse = await articleLicensingApi.createArticleLicense({
          ...formValues,
          newspost: newspost.id,
          billingContactName: fields.billingContactName.value ? fields.billingContactName.value : formValues.submitterName,
          billingContactEmail: fields.billingContactEmail.value ? fields.billingContactEmail.value : formValues.submitterEmail,
        });
        const { licensedNewspostPurchase, paymentIntentClientSecret } = postResponse.data;

        // submit the payment information to Stripe
        const confirmCardPaymentResponse = await confirmCardPayment(
          paymentIntentClientSecret,
          fields.billingContactName.value ? fields.billingContactName.value : formValues.submitterName,
          fields.billingContactEmail.value ? fields.billingContactEmail.value : formValues.submitterEmail,
        );
        if (confirmCardPaymentResponse.error) {
          throw new Error('Error submitting card payment');
        }
        setSubmitted(true);

        // send GTM event
        pushEvent('paymentSubmit', { licensed_newspost_purchase_id: licensedNewspostPurchase.id });

        // navigate to the confirmation screen
        navigate(`${licensingURL}/${licensedNewspostPurchase.checkoutToken}/confirmation`);
      } catch (err) {
        console.error(err);
        setShowNonFieldError(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <div className={`${styles.row} ${styles['payment-container']}`}>
        <div className={`${styles.columns} ${styles['large-7']} ${styles['payment-container-left']}`}>
          <div className={`${styles['checkout-form-wrapper']}`}>
            <SubSection title="Payment information">
              <PaymentInfo
                displayCTA={false}
                validateCardNumber={handleCardNumberChange}
                validateCardExpiry={handleCardExpiryChange}
                validateCardCVC={handleCardCVCChange}
                cardFields={cardFields}
                amountOwed={articleLicensingPriceInDollars * 100}
                contactEmail={articleLicensingContactEmail}
              />
            </SubSection>
            <SubSection title="Billing contact information">
              <BillingContactInfo
                billingContactEmail={fields.billingContactEmail}
                billingContactName={fields.billingContactName}
                billingContactInfoNeeded={billingContactInfoNeeded}
                setBillingContactInfoNeededOnChange={setBillingContactInfoNeededOnChange}
                setField={setField}
                validateField={validateField}
              />
            </SubSection>
            <div key="acceptTermsCheckbox" className={styles['accept-terms']}>
              <ArticleLicensingTermsField
                field={{
                  name: 'acceptTermsCheckbox',
                  value: fields.acceptTermsCheckbox.value,
                  touched: fields.acceptTermsCheckbox.touched,
                  errors: fields.acceptTermsCheckbox.errors,
                }}
                onChange={setField}
                labelContent={(
                  <>
                    I accept the{' '}
                    <a
                      className="analytics t-licensing-policy"
                      href={`https://www.industrydive.com/article-licensing-policy${policySiteParam}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Industry Dive Article Licensing Policy.
                    </a>
                    <span className={styles.required}>*</span>
                  </>
                )}
                descriptionContent="Please review and accept our terms outlined in the Article Licensing Policy."
              />
            </div>
            {showNonFieldError && (
              <MessageBanner messageType="error" onClose={() => setShowNonFieldError(false)}>
                <p>
                  An error has occurred. Please refresh and try again.{' '}
                  If the problem persists, please reach out to&nbsp;
                  <a href={`mailto:${articleLicensingContactEmail}`}>{articleLicensingContactEmail}</a>.
                </p>
              </MessageBanner>
            )}
            <div className={styles['submit-button-wrapper']}>
              <div className={styles['total-price']}>
                <div className={styles['total-price__text']}> Total: </div>
                <div className={styles['total-price__number']}>{`$${articleLicensingPriceInDollars}`}</div>
              </div>
              <Button as="button" type="submit" onClick={handleSubmitClick}>
                Submit
              </Button>
            </div>
          </div>
        </div>
        <div className={`${styles.columns} ${styles['large-5']} ${styles['payment-container-right']}`}>
          <PriceSummaryBox
            purchasedItem="Article Licensing"
            price={articleLicensingPriceInDollars}
            details={newspost.title}
            detailsLink={newspost.url}
            hideTotalMobile
          />
        </div>
      </div>
      {isSubmitting && <LoadingScreen />}
    </>
  );
};

export default PaymentScreen;
